import { ChangeEvent, useState } from 'react';
import Input from '../reusable/Input';
import Dropdown from '../reusable/Dropdown';
import Button from '../reusable/Button';
import '../../styles/reusable/CollapsibleSection.css';

export interface ParameterField {
  name: string;
  label: string;
  type: 'text' | 'number' | 'email' | 'dropdown';
  placeholder?: string;
  required?: boolean;
  options?: { value: string; label: string }[]; // For dropdown type
}

export interface ParameterRow {
  id: string;
  values: Record<string, string | number>;
}

interface DynamicParametersSectionProps {
  fields: ParameterField[];
  values?: Record<string, string | number>; // For single row mode
  rows?: ParameterRow[]; // For multi-row mode
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void; // Single row
  onRowChange?: (rowId: string, name: string, value: string | number) => void; // Multi-row
  onAddRow?: () => void;
  onDeleteRow?: (rowId: string) => void;
  title?: string;
  allowMultipleRows?: boolean;
  defaultExpanded?: boolean;
}

/**
 * Reusable Dynamic Parameters Section
 * Supports two modes:
 * 1. Single row mode: Pass values and onChange
 * 2. Multi-row mode: Pass rows, onRowChange, onAddRow, onDeleteRow with allowMultipleRows=true
 */
const DynamicParametersSection = ({
  fields,
  values,
  rows,
  onChange,
  onRowChange,
  onAddRow,
  onDeleteRow,
  title = 'Select Parameters',
  allowMultipleRows = false,
  defaultExpanded = true,
}: DynamicParametersSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  // Single row mode
  const renderSingleRowField = (field: ParameterField) => {
    const value = values?.[field.name] || '';

    if (field.type === 'dropdown' && field.options) {
      return (
        <Dropdown
          key={field.name}
          label={field.label}
          name={field.name}
          value={String(value)}
          onChange={onChange!}
          options={field.options}
          placeholder={field.placeholder || `Select ${field.label}`}
          required={field.required}
        />
      );
    }

    return (
      <Input
        key={field.name}
        label={field.label}
        name={field.name}
        value={value}
        onChange={onChange!}
        type={field.type}
        placeholder={field.placeholder || `Enter ${field.label}`}
        required={field.required}
      />
    );
  };

  // Multi-row mode
  const renderMultiRowField = (row: ParameterRow, field: ParameterField) => {
    const value = row.values[field.name] || '';

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      onRowChange?.(row.id, field.name, e.target.value);
    };

    if (field.type === 'dropdown' && field.options) {
      return (
        <Dropdown
          key={`${row.id}-${field.name}`}
          label={field.label}
          name={field.name}
          value={String(value)}
          onChange={handleChange}
          options={field.options}
          placeholder={field.placeholder || `Select ${field.label}`}
          required={field.required}
        />
      );
    }

    return (
      <Input
        key={`${row.id}-${field.name}`}
        label={field.label}
        name={field.name}
        value={value}
        onChange={handleChange}
        type={field.type}
        placeholder={field.placeholder || `Enter ${field.label}`}
        required={field.required}
      />
    );
  };

  // Single row mode rendering
  if (!allowMultipleRows) {
    return (
      <div className="collapsible-section">
        <div className="section-header" onClick={() => setIsExpanded(!isExpanded)}>
          <h3>{title}</h3>
          <span className="collapse-icon">{isExpanded ? '−' : '+'}</span>
        </div>
        {isExpanded && (
          <div className="section-content">
            <div className="parameters-grid">
              {fields.map(field => renderSingleRowField(field))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Multi-row mode rendering
  return (
    <div className="collapsible-section">
      <div className="section-header" onClick={() => setIsExpanded(!isExpanded)}>
        <h3>{title}</h3>
        <span className="collapse-icon">{isExpanded ? '−' : '+'}</span>
      </div>
      {isExpanded && (
        <div className="section-content">
          {/* Add Button at Top */}
          <div className="add-row-top">
            <Button
              onClick={onAddRow}
              type="button"
              variant="primary"
              className="add-row-btn-top"
            >
              + Add Row
            </Button>
          </div>

          {/* Parameter Rows */}
          {rows?.map((row) => (
            <div key={row.id} className="parameter-row-wrapper">
              <div className="parameter-row-fields">
                {fields.map(field => renderMultiRowField(row, field))}
                {rows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => onDeleteRow?.(row.id)}
                    className="delete-row-icon-inline"
                    aria-label="Delete row"
                    title="Delete this row"
                  >
                    🗑️
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DynamicParametersSection;
